import * as React from 'react';
import {
    BooleanField,
    BooleanInput,
    Create,
    DateField,
    Edit,
    EditButton,
    FunctionField,
    List,
    NumberInput,
    required,
    SelectField,
    SelectInput,
    SimpleForm,
    TextField,
    TextInput,
    useNotify,
} from 'react-admin';
import AdminPagination from '../AdminPagination';
import AdminSelectableDatagrid from '../AdminSelectableDatagrid';
import FrenchDateTimeInput from '../FrenchDateTimeInput';
import HumanDeleteButton from '../HumanDeleteButton';
import HumanRecordTitle from '../HumanRecordTitle';

import {
    Box,
    Button as MuiButton,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Paper,
    TextField as MuiTextField,
    Typography,
} from '@material-ui/core';
import apiURL from '../apiURL';

const AREA_CHOICES = [
    { id: 'IDF', name: 'Île-de-France' },
    { id: 'FRANCE', name: 'France hors IDF' },
    { id: 'MARTINIQUE', name: 'Martinique' },
    { id: 'GUADELOUPE', name: 'Guadeloupe' },
    { id: 'GUYANE', name: 'Guyane' },
];

const TIMEZONE_CHOICES = [
    { id: 'Europe/Paris', name: 'France métropolitaine — Europe/Paris' },
    { id: 'America/Martinique', name: 'Martinique — America/Martinique' },
    { id: 'America/Guadeloupe', name: 'Guadeloupe — America/Guadeloupe' },
    { id: 'America/Cayenne', name: 'Guyane — America/Cayenne' },
];

const SOURCE_CHOICES = [
    { id: 'manual', name: 'Manuel' },
    { id: 'auto', name: 'Automatique' },
];

const filters = [
    <TextInput
        key="all"
        source="all"
        label="Rechercher"
        alwaysOn
    />,
    <SelectInput
        key="area"
        source="area"
        label="Zone"
        choices={AREA_CHOICES}
    />,
    <SelectInput
        key="active"
        source="active"
        label="État"
        choices={[
            { id: 'true', name: 'Actifs' },
            { id: 'false', name: 'Masqués' },
        ]}
    />,
];


const linesToArray = (value: string): string[] =>
    Array.from(
        new Set(
            String(value || '')
                .split('\n')
                .map(item => item.trim())
                .filter(Boolean)
        )
    );

const FictitiousLibraryPanel = () => {
    const notify = useNotify();

    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);

    const [databaseFirstNames, setDatabaseFirstNames] =
        React.useState<string[]>([]);

    const [form, setForm] = React.useState<any>({
        auto_generation_enabled: false,
        database_first_names: '',
        event_name_bases: '',
        extra_first_names: '',
        extra_nicknames: '',
        extra_regions: '',
    });

    const [generating, setGenerating] =
        React.useState(false);

    const [refreshing, setRefreshing] =
        React.useState(false);

    const [generatorResult, setGeneratorResult] =
        React.useState<any>(null);

    const loadLibrary = React.useCallback(async () => {
        setLoading(true);

        try {
            const token = localStorage.getItem('auth') || '';

            const [settingsResponse, libraryResponse] =
                await Promise.all([
                    fetch(
                        `${apiURL}/fictitiousevent/settings`,
                        {
                            headers: {
                                Accept: 'application/json',
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    ),
                    fetch(
                        `${apiURL}/fictitiousevent/library`,
                        {
                            headers: {
                                Accept: 'application/json',
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    ),
                ]);

            if (
                !settingsResponse.ok ||
                !libraryResponse.ok
            ) {
                throw new Error('HTTP');
            }

            const settings =
                await settingsResponse.json();

            const library =
                await libraryResponse.json();

            setForm({
                auto_generation_enabled:
                    !!settings.auto_generation_enabled,
                database_first_names:
                    Array.isArray(
                        library.enabled_database_first_names
                    )
                        ? library.enabled_database_first_names.join('\n')
                        : '',
                event_name_bases:
                    Array.isArray(settings.event_name_bases)
                        ? settings.event_name_bases.join('\n')
                        : '',
                extra_first_names:
                    Array.isArray(settings.extra_first_names)
                        ? settings.extra_first_names.join('\n')
                        : '',
                extra_nicknames:
                    Array.isArray(settings.extra_nicknames)
                        ? settings.extra_nicknames.join('\n')
                        : '',
                extra_regions:
                    Array.isArray(settings.extra_regions)
                        ? settings.extra_regions.join('\n')
                        : '',
            });

            setDatabaseFirstNames(
                Array.isArray(library.database_first_names)
                    ? library.database_first_names
                    : []
            );
        } catch (error) {
            notify(
                'Impossible de charger la bibliothèque des événements fictifs',
                'warning'
            );
        } finally {
            setLoading(false);
        }
    }, [notify]);

    React.useEffect(() => {
        loadLibrary();
    }, [loadLibrary]);

    const excludedDatabaseFirstNames = () => {
        const enabled =
            new Set(
                linesToArray(
                    form.database_first_names
                ).map(
                    value =>
                        String(value)
                            .trim()
                            .toLocaleLowerCase('fr')
                )
            );

        return databaseFirstNames.filter(
            value =>
                !enabled.has(
                    String(value)
                        .trim()
                        .toLocaleLowerCase('fr')
                )
        );
    };

    const saveLibrary = async () => {
        if (saving) return;

        setSaving(true);

        try {
            const token = localStorage.getItem('auth') || '';

            const response = await fetch(
                `${apiURL}/fictitiousevent/settings`,
                {
                    method: 'PATCH',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        auto_generation_enabled:
                            !!form.auto_generation_enabled,
                        excluded_database_first_names:
                            excludedDatabaseFirstNames(),
                        event_name_bases:
                            linesToArray(
                                form.event_name_bases
                            ),
                        extra_first_names:
                            linesToArray(
                                form.extra_first_names
                            ),
                        extra_nicknames:
                            linesToArray(
                                form.extra_nicknames
                            ),
                        extra_regions:
                            linesToArray(
                                form.extra_regions
                            ),
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(
                    `HTTP ${response.status}`
                );
            }

            notify(
                'Bibliothèque enregistrée',
                'info'
            );

            await loadLibrary();
        } catch (error) {
            notify(
                'Impossible d’enregistrer la bibliothèque',
                'warning'
            );
        } finally {
            setSaving(false);
        }
    };

    const generateNow = async () => {
        if (generating) return;

        setGenerating(true);
        setGeneratorResult(null);

        try {
            const token = localStorage.getItem('auth') || '';

            const response = await fetch(
                `${apiURL}/fictitiousevent/generate`,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(
                    `HTTP ${response.status}`
                );
            }

            const result =
                await response.json();

            setGeneratorResult(result);

            notify(
                `Génération terminée : ${Number(result.created || 0)} événement(s) créé(s)`,
                'info'
            );
        } catch (error) {
            notify(
                'Impossible de lancer la génération',
                'warning'
            );
        } finally {
            setGenerating(false);
        }
    };

    const refreshFutureEvents = async () => {
        if (refreshing || generating || saving) {
            return;
        }

        const confirmed = window.confirm(
            'Rafraîchir les événements fictifs automatiques futurs ?\\n\\n' +
            'Les événements fictifs manuels et les événements déjà passés seront conservés.'
        );

        if (!confirmed) return;

        setRefreshing(true);
        setGeneratorResult(null);

        try {
            const token =
                localStorage.getItem('auth') || '';

            /*
             * Enregistre d'abord la bibliothèque
             * actuellement affichée.
             */
            const settingsResponse = await fetch(
                `${apiURL}/fictitiousevent/settings`,
                {
                    method: 'PATCH',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        auto_generation_enabled:
                            !!form.auto_generation_enabled,
                        excluded_database_first_names:
                            excludedDatabaseFirstNames(),
                        event_name_bases:
                            linesToArray(form.event_name_bases),
                        extra_first_names:
                            linesToArray(form.extra_first_names),
                        extra_nicknames:
                            linesToArray(form.extra_nicknames),
                        extra_regions:
                            linesToArray(form.extra_regions),
                    }),
                }
            );

            if (!settingsResponse.ok) {
                throw new Error(
                    `Settings HTTP ${settingsResponse.status}`
                );
            }

            const response = await fetch(
                `${apiURL}/fictitiousevent/refresh`,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(
                    `Refresh HTTP ${response.status}`
                );
            }

            const result = await response.json();

            setGeneratorResult({
                ...result,
                weeks_processed:
                    Number(
                        result?.generation
                            ?.weeks_processed || 0
                    ),
            });

            notify(
                `${Number(result.deleted || 0)} événement(s) remplacé(s), ` +
                `${Number(result.created || 0)} recréé(s)`,
                'info'
            );

            window.setTimeout(
                () => window.location.reload(),
                700
            );
        } catch (error) {
            console.error(
                '[FictitiousEvents] refresh error',
                error
            );

            notify(
                'Impossible de rafraîchir les événements fictifs',
                'warning'
            );
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <Box mb={2}>
            <Paper style={{ padding: 20 }}>
                <Typography
                    variant="h6"
                    gutterBottom
                >
                    Bibliothèque des événements fictifs
                </Typography>

                <Typography
                    variant="body2"
                    color="textSecondary"
                    paragraph
                >
                    Ces valeurs servent uniquement à fabriquer
                    les événements fictifs. Elles ne créent
                    aucun lien avec un vrai compte utilisateur.
                </Typography>

                <Box
                    mb={3}
                    p={2}
                    style={{
                        border: '1px solid #ddd',
                        borderRadius: 4,
                    }}
                >
                    <Typography
                        variant="h6"
                        gutterBottom
                    >
                        Génération automatique
                    </Typography>

                    <Typography
                        variant="body2"
                        color="textSecondary"
                        paragraph
                    >
                        Minimum une présence par semaine en Île-de-France,
                        France hors IDF, Martinique et Guadeloupe.
                    </Typography>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={
                                    !!form.auto_generation_enabled
                                }
                                onChange={event =>
                                    setForm({
                                        ...form,
                                        auto_generation_enabled:
                                            event.target.checked,
                                    })
                                }
                                color="primary"
                            />
                        }
                        label="Activer la génération automatique"
                    />

                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ marginTop: 8 }}
                    >
                        Le système choisit automatiquement entre 4 et 8
                        événements fictifs par semaine et maintient
                        plusieurs semaines d’avance.
                    </Typography>

                    <Box mt={2}>
                        <MuiButton
                            variant="outlined"
                            color="primary"
                            disabled={
                                generating ||
                                loading
                            }
                            onClick={generateNow}
                        >
                            {generating
                                ? 'Génération en cours…'
                                : 'Compléter maintenant'}
                        </MuiButton>

                          <MuiButton
                              variant="outlined"
                              color="secondary"
                              disabled={
                                  refreshing ||
                                  generating ||
                                  saving ||
                                  loading
                              }
                              onClick={refreshFutureEvents}
                              style={{
                                  marginLeft: 12,
                              }}
                          >
                              {refreshing
                                  ? 'Rafraîchissement…'
                                  : 'Rafraîchir les événements'}
                          </MuiButton>
                    </Box>

                    {generatorResult && (
                        <Box mt={2}>
                            <Typography variant="body2">
                                Dernier résultat :{' '}
                                {Number(generatorResult.created || 0)}
                                {' '}créé(s) —{' '}
                                {Number(generatorResult.weeks_processed || 0)}
                                {' '}semaine(s) contrôlée(s).
                            </Typography>
                        </Box>
                    )}
                </Box>

                {loading ? (
                    <Box
                        display="flex"
                        alignItems="center"
                        p={2}
                    >
                        <CircularProgress size={24} />
                        <Box ml={2}>
                            Chargement…
                        </Box>
                    </Box>
                ) : (
                    <>
                        <Box
                            display="flex"
                            flexWrap="wrap"
                            style={{ gap: 20 }}
                        >
                            <Box
                                flex="1 1 280px"
                                minWidth={260}
                            >
                                <MuiTextField
                                    label="Noms d’événements — un par ligne"
                                    value={form.event_name_bases}
                                    onChange={event =>
                                        setForm({
                                            ...form,
                                            event_name_bases:
                                                event.target.value,
                                        })
                                    }
                                    multiline
                                    rows={10}
                                    variant="outlined"
                                    fullWidth
                                />
                            </Box>

                            <Box
                                flex="1 1 280px"
                                minWidth={260}
                            >
                                <MuiTextField
                                    label="Prénoms supplémentaires — un par ligne"
                                    value={form.extra_first_names}
                                    onChange={event =>
                                        setForm({
                                            ...form,
                                            extra_first_names:
                                                event.target.value,
                                        })
                                    }
                                    multiline
                                    rows={10}
                                    variant="outlined"
                                    fullWidth
                                />
                            </Box>

                            <Box
                                flex="1 1 280px"
                                minWidth={260}
                            >
                                <MuiTextField
                                    label="Surnoms — un par ligne"
                                    value={form.extra_nicknames}
                                    onChange={event =>
                                        setForm({
                                            ...form,
                                            extra_nicknames:
                                                event.target.value,
                                        })
                                    }
                                    multiline
                                    rows={10}
                                    variant="outlined"
                                    fullWidth
                                />
                            </Box>

                            <Box
                                flex="1 1 280px"
                                minWidth={260}
                            >
                                <MuiTextField
                                    label="Régions supplémentaires — une par ligne"
                                    value={form.extra_regions}
                                    onChange={event =>
                                        setForm({
                                            ...form,
                                            extra_regions:
                                                event.target.value,
                                        })
                                    }
                                    multiline
                                    rows={10}
                                    variant="outlined"
                                    fullWidth
                                    helperText="En complément de la France métropolitaine, Corse, Guadeloupe, Martinique et Guyane."
                                />
                            </Box>
                        </Box>

                        <Box mt={3}>
                            <MuiTextField
                                label="Prénoms provenant de la base utilisateurs — un par ligne"
                                value={form.database_first_names}
                                onChange={event =>
                                    setForm({
                                        ...form,
                                        database_first_names:
                                            event.target.value,
                                    })
                                }
                                multiline
                                rows={6}
                                variant="outlined"
                                fullWidth
                                helperText={
                                    `Supprimer un prénom ici l’exclut uniquement du générateur fictif. ` +
                                    `Le vrai compte utilisateur n’est jamais modifié. ` +
                                    `Pour ajouter un nouveau prénom, utilisez « Prénoms supplémentaires ».`
                                }
                            />
                        </Box>

                        <Box mt={3}>
                            <MuiButton
                                variant="contained"
                                color="primary"
                                disabled={saving}
                                onClick={saveLibrary}
                            >
                                {saving
                                    ? 'Enregistrement…'
                                    : 'Enregistrer la bibliothèque'}
                            </MuiButton>
                        </Box>
                    </>
                )}
            </Paper>
        </Box>
    );
};

const FictitiousStatusField = (props: any) => (
    <FunctionField
        {...props}
        render={(record: any) => {
            if (!record?.date || !record?.end) return '—';

            const now = Date.now();
            const start = new Date(record.date).getTime();
            const end = new Date(record.end).getTime();

            if (now < start) return 'À venir';
            if (now < end) return 'En cours';
            return 'Terminé';
        }}
    />
);

const EventForm = () => (
    <>
        <TextInput
            source="name"
            label="Nom de l’événement"
            helperText="Exemple : Karaoké chez Mika, Anniversaire, Soirée entre amis..."
            validate={required()}
            fullWidth
        />

        <TextInput
            source="first_name"
            label="Prénom"
            helperText="Prénom utilisé uniquement pour fabriquer les événements fictifs."
        />

        <TextInput
            source="nickname"
            label="Surnom"
            helperText="Exemple : Mika, Jo, Chris..."
        />

        <SelectInput
            source="area"
            label="Zone"
            choices={AREA_CHOICES}
            validate={required()}
        />

        <TextInput
            source="city"
            label="Ville"
            validate={required()}
            fullWidth
        />

        <SelectInput
            source="timezone"
            label="Fuseau horaire"
            choices={TIMEZONE_CHOICES}
            validate={required()}
            defaultValue="Europe/Paris"
            fullWidth
        />

        <FrenchDateTimeInput
            source="date"
            label="Date et heure de début"
            validate={required()}
            required
        />

        <NumberInput
            source="duration_minutes"
            label="Durée fictive (minutes)"
            helperText="Durée utilisée uniquement pour afficher l’événement EN COURS dans l’agenda."
            defaultValue={180}
            min={30}
            max={720}
            validate={required()}
        />

        <BooleanInput
            source="active"
            label="Afficher dans l’agenda"
            defaultValue={true}
        />
    </>
);

export const FictitiousEventList = (props: any) => (
    <>
        <FictitiousLibraryPanel />

        <List
        {...props}
        title="Événements fictifs"
        filters={filters}
        bulkActionButtons={false}
        sort={{ field: 'date', order: 'ASC' }}
        perPage={50}
        pagination={<AdminPagination />}
        exporter={false}
    >
        <AdminSelectableDatagrid>
            <DateField
                source="date"
                label="Date"
                showTime
            />

            <TextField
                source="name"
                label="Événement"
            />

            <TextField
                source="nickname"
                label="Surnom"
                emptyText="—"
            />

            <TextField
                source="city"
                label="Ville"
            />

            <SelectField
                source="area"
                label="Zone"
                choices={AREA_CHOICES}
            />

            <FictitiousStatusField
                label="Statut"
            />

            <SelectField
                source="source"
                label="Création"
                choices={SOURCE_CHOICES}
            />

            <BooleanField
                source="active"
                label="Agenda"
            />

            <EditButton />

            <HumanDeleteButton
                fields={['name']}
                fallback="cet événement fictif"
            />
        </AdminSelectableDatagrid>
        </List>
    </>
);

export const FictitiousEventCreate = (props: any) => (
    <Create
        {...props}
        title="Nouvel événement fictif"
    >
        <SimpleForm redirect="list">
            <EventForm />
        </SimpleForm>
    </Create>
);

export const FictitiousEventEdit = (props: any) => (
    <Edit
        {...props}
        mutationMode="optimistic"
        title={
            <HumanRecordTitle
                fields={['name']}
                fallback="Événement fictif"
            />
        }
    >
        <SimpleForm redirect="list">
            <EventForm />
        </SimpleForm>
    </Edit>
);
